/**
 * City autocomplete integration
 * Combines nearby dropdown with EasyAutocomplete for typing
 */

import { createNearbyDropdown } from './nearbyDropdown.js';

export function initializeCityAutocomplete($field, options = {}) {
    const {
        getCoordinates = () => ({ lat: null, lon: null }),
        onCitySelect = () => {},
        onCityClear = () => {},
        isHomeCity = false,
        initialCountryIso = ''
    } = options;

    // Mark as initialized to prevent duplicates
    if ($field.data('ac-initialized')) {
        return null;
    }
    $field.data('ac-initialized', true);

    // Helper to update location icon visibility
    function updateLocationIcon(hasFlag) {
        const $container = $field.closest('.easy-autocomplete');
        const $icon = $container.find('.location-icon');
        if (hasFlag) {
            $icon.hide();
        } else {
            $icon.show();
        }
    }

    // Helper to apply flag to input and hide location icon
    function applyFlag(countryIso) {
        if (countryIso) {
            const flagUrl = `/images/circle-flags/flags/${countryIso}.svg`;
            $field.data('selected-flag', flagUrl);
            $field.data('selected-country-iso', countryIso);
            $field.css({
                'background-image': `url("${flagUrl}")`,
                'background-repeat': 'no-repeat',
                'background-position': 'left 12px center',
                'background-size': '20px 20px',
                'padding-left': '40px'
            });
            updateLocationIcon(true);
        }
    }

    // Helper to clear flag from input and show location icon
    function clearFlag() {
        $field.removeData('selected-flag');
        $field.removeData('selected-country-iso');
        $field.css({
            'background-image': '',
            'padding-left': ''
        });
        updateLocationIcon(false);
    }

    // Helper to apply stored flag to input
    function applyStoredFlag() {
        const storedFlag = $field.data('selected-flag');
        if (storedFlag) {
            $field.css({
                'background-image': `url("${storedFlag}")`,
                'background-repeat': 'no-repeat',
                'background-position': 'left 12px center',
                'background-size': '20px 20px',
                'padding-left': '40px'
            });
            updateLocationIcon(true);
        } else {
            updateLocationIcon(false);
        }
    }

    // Create nearby dropdown with extended onCitySelect that includes countryIso
    const nearbyDropdown = createNearbyDropdown($field, {
        getCoordinates,
        onCitySelect: (cityId, cityName, countryIso) => {
            onCitySelect(cityId, cityName, countryIso);
        },
        isHomeCity
    });

    nearbyDropdown.init();

    // Handle focus - show nearby cities if empty
    $field.on('focus.nearby', function() {
        const val = $(this).val().trim();
        if (!val) {
            nearbyDropdown.show();
        }
    });

    // Hide dropdown on blur
    $field.on('blur.nearby', function() {
        setTimeout(() => nearbyDropdown.hide(), 200);
    });

    // Hide on ESC key
    $field.on('keydown.nearby', function(e) {
        if (e.keyCode === 27) {
            nearbyDropdown.hide();
            e.preventDefault();
        }
    });

    // Hide when typing, and clear flag if input is emptied
    $field.on('input.nearby', function() {
        const val = $(this).val().trim();
        if (val.length > 0) {
            nearbyDropdown.hide();
        }
        // Clear flag and city data if input is emptied
        if (val.length === 0) {
            clearFlag();
            onCityClear();
        }
    });

    // Initialize EasyAutocomplete for typing
    if (typeof $field.easyAutocomplete === 'function' && typeof genCityAcOptions === 'function') {
        const acOptions = genCityAcOptions($field, {
            includeNearest: true,
            getLatLon: getCoordinates
        });

        acOptions.minCharNumber = 1;

        // Use onChooseEvent instead of onSelectItemEvent to update Alpine model
        // onSelectItemEvent fires on both hover and click, causing the flag to change on hover
        // onChooseEvent only fires on actual selection (click or Enter key)
        const originalOnChoose = acOptions.list.onChooseEvent;
        acOptions.list.onChooseEvent = function() {
            if (originalOnChoose) {
                originalOnChoose.call(this);
            }

            const selectedData = $field.getSelectedItemData();
            if (selectedData && selectedData.id) {
                const countryIso = selectedData.country_iso || '';
                onCitySelect(String(selectedData.id), $field.val(), countryIso);

                // Apply flag icon
                if (countryIso) {
                    applyFlag(countryIso);
                }
            }
        };

        // Override onShowListEvent to preserve the flag
        const originalOnShowList = acOptions.list.onShowListEvent;
        acOptions.list.onShowListEvent = function() {
            if (originalOnShowList) {
                originalOnShowList.call(this);
            }
            // Re-apply the stored flag after the original handler clears background
            applyStoredFlag();
        };

        // Override onHideListEvent to preserve the flag
        const originalOnHideList = acOptions.list.onHideListEvent;
        acOptions.list.onHideListEvent = function() {
            if (originalOnHideList) {
                originalOnHideList.call(this);
            }
            // Re-apply the stored flag after the original handler clears background
            applyStoredFlag();
        };

        $field.easyAutocomplete(acOptions);
    }

    // Add location icon (must be after easyAutocomplete creates the container)
    const $container = $field.closest('.easy-autocomplete');
    if ($container.length && !$container.find('.location-icon').length) {
        $container.css('position', 'relative');
        $container.append('<i class="fa fa-map-marker location-icon"></i>');
    }

    // Apply flag and hide location icon if country ISO is set (e.g., restored from saved state)
    // This must happen AFTER the icon is added to the DOM
    if (initialCountryIso) {
        applyFlag(initialCountryIso);
    }

    // Public API
    return {
        destroy() {
            $field.off('.nearby');
            nearbyDropdown.destroy();
            $field.removeData('ac-initialized');
        },
        showNearby() {
            nearbyDropdown.show();
        },
        showWaiting() {
            nearbyDropdown.showWaiting();
        }
    };
}

export function cleanupCityAutocomplete($field) {
    $field.off('.nearby');
    const $parent = $field.parent();
    if ($parent.hasClass('nearby-dropdown-container')) {
        $parent.find('.nearby-dropdown').remove();
        $field.unwrap();
    }
    $field.removeData('ac-initialized');
}
